import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Search, RefreshCw, History, Trash2, ArrowRight, AtSign, Filter } from "lucide-react";

const ACTIONS = [
  { key: "all", label: "Todas" },
  { key: "status_change", label: "Mudança de status", icon: ArrowRight },
  { key: "delete", label: "Exclusão", icon: Trash2 },
  { key: "email_rename", label: "Renomeação de e-mail", icon: AtSign },
];

const ENTITY_TYPES = [
  { key: "all", label: "Todos" },
  { key: "subscription", label: "Inscrição" },
  { key: "order", label: "Pedido" },
  { key: "bulk", label: "Em massa" },
];

const STATUS_LABEL = {
  pending: "Pendente",
  paid: "Pago",
  cancelled: "Cancelado",
  "": "—",
};

const ACTION_STYLE = {
  status_change: "bg-amber-100 text-amber-900 border-amber-200",
  delete: "bg-red-100 text-red-900 border-red-200",
  email_rename: "bg-sky-100 text-sky-900 border-sky-200",
};

function formatTS(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "medium" });
  } catch {
    return iso;
  }
}

function StatusPill({ value }) {
  const v = value || "";
  const cls = v === "paid"
    ? "bg-emerald-100 text-emerald-800 border-emerald-200"
    : v === "pending"
    ? "bg-amber-100 text-amber-800 border-amber-200"
    : v === "cancelled"
    ? "bg-red-100 text-red-800 border-red-200"
    : "bg-stone-200 text-stone-600 border-stone-300";
  return (
    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${cls}`}>
      {STATUS_LABEL[v] || v}
    </span>
  );
}

function ActionBadge({ action }) {
  const cls = ACTION_STYLE[action] || "bg-stone-100 text-stone-800 border-stone-200";
  const found = ACTIONS.find((a) => a.key === action);
  const Icon = found?.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border font-semibold ${cls}`}>
      {Icon ? <Icon className="h-3 w-3" /> : null}
      {found?.label || action}
    </span>
  );
}

function ChangeSummary({ row }) {
  if (row.action === "status_change") {
    const before = row.before?.status || "";
    const after = row.after?.status || "";
    return (
      <div className="flex items-center gap-2 text-xs">
        <StatusPill value={before} />
        <ArrowRight className="h-3.5 w-3.5 text-stone-400" />
        <StatusPill value={after} />
      </div>
    );
  }
  if (row.action === "email_rename") {
    return (
      <div className="text-xs text-stone-700 font-mono truncate">
        <span className="line-through text-stone-400 mr-1">{row.before?.email}</span>
        <ArrowRight className="inline h-3 w-3 text-stone-400 mx-1" />
        <span className="font-semibold">{row.after?.email}</span>
      </div>
    );
  }
  if (row.action === "delete") {
    const parts = [];
    if (row.before?.status) parts.push(`status ${STATUS_LABEL[row.before.status] || row.before.status}`);
    if (row.before?.items_count) parts.push(`${row.before.items_count} item(ns)`);
    if (row.before?.total) parts.push(`R$ ${Number(row.before.total).toFixed(2).replace(".", ",")}`);
    return (
      <div className="text-xs text-stone-700">
        <span className="text-red-700 font-semibold">Excluído</span>
        {parts.length > 0 && <span className="text-stone-500"> · {parts.join(" · ")}</span>}
      </div>
    );
  }
  return <div className="text-xs text-stone-500">—</div>;
}

export default function ManageAuditLogs() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    q: "",
    action: "all",
    entity_type: "all",
    actor: "all",
    date_from: "",
    date_to: "",
  });

  const buildParams = useCallback(() => {
    const p = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v && v !== "all") p[k] = v;
    });
    return p;
  }, [filters]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: list }, { data: sum }] = await Promise.all([
        api.get("/audit-logs", { params: buildParams() }),
        api.get("/audit-logs/summary"),
      ]);
      setRows(list || []);
      setSummary(sum || null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro ao carregar logs");
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    load();
  }, [load]);

  const clearFilters = () => {
    setFilters({ q: "", action: "all", entity_type: "all", actor: "all", date_from: "", date_to: "" });
  };

  const hasActiveFilters = useMemo(() => {
    return filters.q || filters.action !== "all" || filters.entity_type !== "all" || filters.actor !== "all" || filters.date_from || filters.date_to;
  }, [filters]);

  const actorOptions = useMemo(() => {
    const base = [{ key: "all", label: "Todos" }];
    (summary?.actors || []).forEach((a) => base.push({ key: a, label: a }));
    return base;
  }, [summary]);

  return (
    <div className="space-y-8" data-testid="manage-audit-logs">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-orange-700 font-semibold">Segurança</div>
          <h1 className="font-display text-4xl font-bold mt-2">Log de auditoria</h1>
          <p className="text-stone-600 mt-2">Rastreie exclusões, mudanças de status e renomeações de e-mail.</p>
        </div>
        <Button
          variant="outline"
          onClick={() => load()}
          disabled={loading}
          className="rounded-full"
          data-testid="audit-refresh-btn"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Atualizar
        </Button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="audit-summary">
        {[
          { key: "status_change", label: "Mudanças de status" },
          { key: "delete", label: "Exclusões" },
          { key: "email_rename", label: "Renomeações" },
        ].map((k) => {
          const count = summary?.by_action?.[k.key] || 0;
          return (
            <Card key={k.key} className="border-stone-200 rounded-2xl">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-red-700 to-orange-600 flex items-center justify-center">
                  <ActionBadgeIcon action={k.key} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-stone-500 font-semibold">{k.label}</div>
                  <div className="font-display text-2xl font-bold text-stone-900">{count}</div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filters */}
      <Card className="border-stone-200 rounded-2xl">
        <CardContent className="p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="lg:col-span-2">
              <Label className="text-xs">Buscar (nome, email, comprovante, ID)</Label>
              <div className="relative mt-1">
                <Search className="h-4 w-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  value={filters.q}
                  onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                  placeholder="Digite para buscar..."
                  className="pl-9"
                  data-testid="audit-search-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Data inicial</Label>
              <Input
                type="date"
                value={filters.date_from}
                onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                className="mt-1"
                data-testid="audit-date-from"
              />
            </div>
            <div>
              <Label className="text-xs">Data final</Label>
              <Input
                type="date"
                value={filters.date_to}
                onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                className="mt-1"
                data-testid="audit-date-to"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs">Tipo de ação</Label>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {ACTIONS.map((a) => (
                  <button
                    key={a.key}
                    type="button"
                    onClick={() => setFilters({ ...filters, action: a.key })}
                    className={`h-7 px-2.5 rounded-full text-[11px] font-semibold border transition-all ${
                      filters.action === a.key
                        ? "bg-gradient-to-r from-red-700 to-orange-600 text-white border-transparent shadow-sm"
                        : "bg-white text-stone-700 border-stone-300 hover:border-orange-500 hover:text-orange-700"
                    }`}
                    data-testid={`audit-action-${a.key}`}
                  >
                    {a.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs">Entidade</Label>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {ENTITY_TYPES.map((e) => (
                  <button
                    key={e.key}
                    type="button"
                    onClick={() => setFilters({ ...filters, entity_type: e.key })}
                    className={`h-7 px-2.5 rounded-full text-[11px] font-semibold border transition-all ${
                      filters.entity_type === e.key
                        ? "bg-gradient-to-r from-red-700 to-orange-600 text-white border-transparent shadow-sm"
                        : "bg-white text-stone-700 border-stone-300 hover:border-orange-500 hover:text-orange-700"
                    }`}
                    data-testid={`audit-entity-${e.key}`}
                  >
                    {e.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs">Responsável</Label>
              <select
                value={filters.actor}
                onChange={(e) => setFilters({ ...filters, actor: e.target.value })}
                className="mt-1 w-full h-9 rounded-md border border-stone-300 bg-white px-2 text-sm"
                data-testid="audit-actor-select"
              >
                {actorOptions.map((a) => (
                  <option key={a.key} value={a.key}>{a.label}</option>
                ))}
              </select>
            </div>
          </div>

          {hasActiveFilters && (
            <div className="flex justify-end">
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-stone-600"
                data-testid="audit-clear-filters"
              >
                <Filter className="h-3.5 w-3.5 mr-1" /> Limpar filtros
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Rows */}
      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
        </div>
      ) : rows.length === 0 ? (
        <Card className="border-dashed border-stone-300 rounded-2xl">
          <CardContent className="py-16 text-center">
            <History className="h-10 w-10 text-stone-400 mx-auto" />
            <div className="font-display text-xl mt-4 text-stone-700">Nenhum evento encontrado</div>
            <p className="text-stone-500 text-sm mt-2">Ajuste os filtros ou aguarde novas ações.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          <div className="text-sm text-stone-600 px-1">
            <span className="font-semibold text-stone-900">{rows.length}</span> evento(s) na lista
          </div>
          <div className="space-y-2" data-testid="audit-list">
            {rows.map((r) => (
              <Card key={r.id} className="border-stone-200 rounded-2xl" data-testid={`audit-row-${r.id}`}>
                <CardContent className="p-4">
                  <div className="grid grid-cols-1 lg:grid-cols-[180px_1fr_1fr] gap-4">
                    <div>
                      <div className="text-[11px] text-stone-500 font-mono">{formatTS(r.timestamp)}</div>
                      <div className="mt-1"><ActionBadge action={r.action} /></div>
                      <div className="text-[10px] uppercase tracking-widest text-stone-400 mt-1">
                        {r.entity_type === "subscription" ? "Inscrição" : r.entity_type === "order" ? "Pedido" : r.entity_type === "bulk" ? "Em massa" : r.entity_type}
                      </div>
                    </div>
                    <div className="min-w-0">
                      <div className="font-display font-semibold text-stone-900 truncate">{r.customer_name || "—"}</div>
                      {r.customer_email && (
                        <div className="text-xs text-stone-500 mt-0.5 truncate">{r.customer_email}</div>
                      )}
                      {r.metadata?.txid && (
                        <div className="text-[10px] font-mono text-stone-400 mt-1 truncate">{r.metadata.txid}</div>
                      )}
                      <div className="text-[10px] text-stone-400 mt-1">
                        Por: <span className="font-mono">{r.actor_email || "?"}</span>
                        {r.actor_role ? <span className="ml-1 uppercase tracking-widest">({r.actor_role})</span> : null}
                      </div>
                    </div>
                    <div className="min-w-0">
                      <ChangeSummary row={r} />
                      {r.action === "email_rename" && r.metadata && (
                        <div className="text-[10px] text-stone-500 mt-1.5">
                          Registros atualizados: <b>{(r.metadata.subscriptions_updated || 0) + (r.metadata.orders_updated || 0)}</b>
                          {" "}({r.metadata.subscriptions_updated || 0} inscr, {r.metadata.orders_updated || 0} pedidos)
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ActionBadgeIcon({ action }) {
  if (action === "delete") return <Trash2 className="h-5 w-5 text-white" />;
  if (action === "email_rename") return <AtSign className="h-5 w-5 text-white" />;
  return <ArrowRight className="h-5 w-5 text-white" />;
}
